import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, FormHelperText, Typography } from "@mui/material";

import CloseButton from "../../../Components/CloseButton";
import {
    StyledDialog,
    StyledTextField,
} from "../../../Components/EditVehicleDetails/EditDialogShared";
import {
    CancelFormActionButton,
    PrimaryFormLoadingActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import SerialReportTable from "./SerialReportTable";
import {
    canReviewWorkOrder,
    formatWorkOrderDateTime,
    summariseSerialReport,
    workOrderStatusText,
} from "./workOrderHelpers";
import { reviewWorkOrder } from "../../../Api/WorkOrderApi";
import { usePermissions } from "../../../Context/PermissionContext";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import { STATUS_PALETTE } from "../statusPalette";
import { FieldLabel, FORM_TOKENS } from "./WorkOrderFormShared";

// Approve or reject one pending work order. The only call in the module that
// moves asset state.
//
// TWO GATES, both of which the backend enforces (reviewWorkOrder steps 3 and 4):
//
//   `approve` on WORK_ORDER.<TYPE>   -- a per-type grant, and
//       Casbin's dotted codes do NOT inherit, so holding it on dispatches gives
//       nothing on pack replacements.
//   the reviewer is not the maker                     -- an invariant, backed by
//       the ck_wo_not_self_approved CHECK constraint, and true even when one
//       role legitimately carries both create and approve on the same type.
//
// Offering a button that will 403 is worse than not offering one. But a button
// that silently vanishes is its own bug report, so the self-review case SAYS SO
// rather than hiding: that is the one a user will otherwise raise a ticket
// about, because they can see the row and they do hold the permission.
//
// Approve can still fail with a 409 carrying the SAME {summary, serials} body
// the create 422 carries: custody can have moved in the days since the request
// was raised, and approval re-resolves every serial rather than trusting the
// snapshot. It renders through the same SerialReportTable, because it is the
// same report.

// The dialog is wider than the 560px house default: a serial report inside a
// 560px column is a horizontal scrollbar over the only diagnosis the API gives.
const WIDE_PAPER = { "& .MuiPaper-root": { width: "760px", maxWidth: "94vw" } };

const Field = ({ label, value }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "12px", color: FORM_TOKENS.inkLabel }}>{label}</Typography>
        <Typography
            sx={{ fontSize: "14px", fontWeight: 500, color: FORM_TOKENS.ink, overflowWrap: "anywhere" }}
        >
            {value}
        </Typography>
    </Box>
);

const ReviewWorkOrderDialog = ({
    open,
    workOrder,
    currentUserID,
    onClose,
    onReviewed,
}) => {
    const { hasPermission } = usePermissions();
    const { openNotification } = useToast();

    const [reviewRemark, setReviewRemark] = useState("");
    const [remarkError, setRemarkError] = useState("");
    const [pendingAction, setPendingAction] = useState(null);
    const [serialReport, setSerialReport] = useState(null);

    useEffect(() => {
        if (!open) return;
        setReviewRemark("");
        setRemarkError("");
        setPendingAction(null);
        setSerialReport(null);
    }, [open, workOrder?.id]);

    // Recomputed here rather than trusted from the caller: this dialog can be
    // opened from the queue row or from the detail page, and a gate that lives
    // in one of them is a gate the other can forget.
    const gate = useMemo(
        () => canReviewWorkOrder({ row: workOrder, currentUserID, hasPermission }),
        [workOrder, currentUserID, hasPermission]
    );

    const submit = useCallback(
        async (action) => {
            // REQUIRED ON BOTH, here. The API requires it only to reject --
            // an integration approving in bulk is not forced to invent a
            // sentence -- but a person standing in front of this dialog is
            // making a judgement the maker cannot see, and the one place to
            // record it is this box.
            //
            // Validated INLINE before sending: a round trip to be told a
            // required box is empty is a round trip for nothing.
            const remark = reviewRemark.trim();
            if (!remark) {
                setRemarkError(
                    action === "reject"
                        ? "Say why this is being rejected"
                        : "Say why this is being approved"
                );
                return;
            }

            setRemarkError("");
            setPendingAction(action);
            try {
                const res = await reviewWorkOrder(workOrder.id, {
                    action,
                    reviewRemark: remark,
                });

                if (res?.responseStatus?.code === CODE.SUCCESS) {
                    setSerialReport(null);
                    openNotification({
                        text:
                            res?.responseStatus?.msg ||
                            (action === "approve"
                                ? "Work order approved"
                                : "Work order rejected"),
                        icon: "toast-success",
                    });
                    onReviewed?.(action);
                    return;
                }

                // The 409 serial report. Detected by SHAPE, not by status code
                // -- the identical body arrives as a 422 from create -- and
                // rendered, never stringified.
                const report = summariseSerialReport(res);
                if (report) {
                    setSerialReport(report);
                    return;
                }

                setSerialReport(null);
                openNotification({
                    text:
                        res?.responseStatus?.msg ||
                        `Could not ${action} the work order`,
                    icon: "toast-error",
                });
            } finally {
                setPendingAction(null);
            }
        },
        [workOrder, reviewRemark, openNotification, onReviewed]
    );

    if (!workOrder) return null;

    const busy = Boolean(pendingAction);

    return (
        <StyledDialog open={open} onClose={() => !busy && onClose?.()} sx={WIDE_PAPER}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography
                    variant="fw600s16c000000"
                    sx={{ fontSize: "20px", lineHeight: "24px", color: FORM_TOKENS.inkStrong }}
                >
                    Review {workOrder.workOrderReferenceID || "work order"}
                </Typography>
                <CloseButton onClick={() => !busy && onClose?.()} />
            </Box>

            <Divider sx={{ my: 2.5, borderColor: FORM_TOKENS.fieldBorder }} />

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    columnGap: 3,
                    rowGap: 2,
                    mb: 2,
                }}
            >
                <Field label="Type" value={workOrder.workOrderTypeLabel || workOrder.workOrderTypeCode || "-"} />
                <Field label="Status" value={workOrderStatusText(workOrder)} />
                <Field label="Assets" value={workOrder.itemCount ?? "-"} />
                <Field label="Raised By" value={workOrder.createdByName || "-"} />
                <Field label="Raised On" value={formatWorkOrderDateTime(workOrder.createdAt)} />
                <Field label="Remarks" value={workOrder.remarks || "-"} />
            </Box>

            {gate.allowed ? (
                <>
                    {/* ONE BOX FOR EITHER DECISION. It used to say "Reason
                        for rejection" and be ignored on approval, which left
                        an approved work order carrying no word from the person
                        who approved it. */}
                    <FieldLabel label="Your remark" required />
                    <StyledTextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        disabled={busy}
                        placeholder="What the maker should know about this decision"
                        value={reviewRemark}
                        error={Boolean(remarkError)}
                        onChange={(e) => {
                            setReviewRemark(e.target.value);
                            if (remarkError) setRemarkError("");
                        }}
                        inputProps={{ "data-testid": "work-order-review-remark" }}
                    />
                    <FormHelperText
                        sx={{ fontSize: "11px", ml: 0, color: remarkError ? FORM_TOKENS.danger : FORM_TOKENS.inkFaint }}
                    >
                        {remarkError ||
                            "Recorded either way, and shown to the maker. A rejection writes no asset history — the request stays as a record of what was asked for."}
                    </FormHelperText>
                </>
            ) : (
                <Box
                    sx={{
                        border: `1px solid ${FORM_TOKENS.fieldBorder}`,
                        borderRadius: "8px",
                        backgroundColor: FORM_TOKENS.panel,
                        p: 2,
                    }}
                >
                    <Typography sx={{ fontSize: "13px", color: FORM_TOKENS.inkLabel }}>
                        {gate.reason ||
                            (workOrder.status !== "pending"
                                ? `This work order has already been ${workOrder.status}.`
                                : "You do not have permission to review this work order type.")}
                    </Typography>
                </Box>
            )}

            {/* The 409 report: custody moved between the request and this
                click. Same component as the create form's 422. */}
            <SerialReportTable
                report={serialReport}
                title="Approval was refused — these serials no longer resolve"
                testIdPrefix="work-order-review-serial-report"
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
                <CancelFormActionButton
                    data-testid="work-order-review-close"
                    onClick={() => onClose?.()}
                    disabled={busy}
                >
                    Close
                </CancelFormActionButton>
                {gate.allowed && (
                    <>
                        <CancelFormActionButton
                            data-testid="work-order-reject"
                            onClick={() => submit("reject")}
                            disabled={busy}
                            sx={{
                                color: FORM_TOKENS.danger,
                                borderColor: STATUS_PALETTE.error.border,
                            }}
                        >
                            {pendingAction === "reject" ? "Rejecting…" : "Reject"}
                        </CancelFormActionButton>
                        <PrimaryFormLoadingActionButton
                            data-testid="work-order-approve"
                            onClick={() => submit("approve")}
                            loading={pendingAction === "approve"}
                            disabled={busy}
                        >
                            Approve
                        </PrimaryFormLoadingActionButton>
                    </>
                )}
            </Box>
        </StyledDialog>
    );
};

export default ReviewWorkOrderDialog;
